import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Zap, LogOut, CalendarCheck, MessageSquare, MessageCircle, Loader2, Inbox } from "lucide-react";
import { REVIEW_LINK } from "@/lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const api = axios.create({ withCredentials: true });

api.interceptors.response.use(
  (r) => r,
  async (err) => {
    const original = err.config || {};
    const url = original.url || "";
    if (err.response?.status === 401 && !original._retried && !url.includes("/auth/login") && !url.includes("/auth/refresh")) {
      original._retried = true;
      try {
        await api.post(`${API}/auth/refresh`);
        return api(original);
      } catch {
        return Promise.reject(err);
      }
    }
    return Promise.reject(err);
  }
);

const formatDetail = (d) => {
  if (d == null) return "Something went wrong. Please try again.";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => (e && e.msg) || JSON.stringify(e)).join(" ");
  return d.msg || String(d);
};

const STATUS_STYLES = {
  requested: "bg-ember/10 text-ember border-ember/40",
  confirmed: "bg-volt/10 text-volt border-volt/40",
  completed: "bg-[#25D366]/10 text-[#25D366] border-[#25D366]/40",
  cancelled: "bg-red-500/10 text-red-400 border-red-500/40",
};

const inputCls =
  "w-full bg-obsidian border border-white/15 rounded-sm px-4 py-3 text-sm text-white placeholder:text-mist/50 focus:outline-none focus:border-ember transition-colors";

export default function AdminPage() {
  const [user, setUser] = useState(undefined);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("bookings");
  const [bookings, setBookings] = useState([]);
  const [messages, setMessages] = useState([]);

  const loadData = useCallback(async () => {
    const [b, m] = await Promise.all([api.get(`${API}/bookings`), api.get(`${API}/messages`)]);
    setBookings([...b.data].reverse());
    setMessages([...m.data].reverse());
  }, []);

  useEffect(() => {
    api.get(`${API}/auth/me`).then((r) => setUser(r.data)).catch(() => setUser(null));
  }, []);

  useEffect(() => {
    if (user) loadData().catch(() => toast.error("Couldn't load dashboard data"));
  }, [user, loadData]);

  const login = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post(`${API}/auth/login`, { email, password });
      setUser(data);
      toast.success(`Welcome back, ${data.name || "admin"}`);
    } catch (err) {
      setError(formatDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const logout = async () => {
    await api.post(`${API}/auth/logout`).catch(() => {});
    setUser(null);
  };

  const setStatus = async (id, status) => {
    try {
      await api.patch(`${API}/bookings/${id}`, { status });
      setBookings((bs) => bs.map((b) => (b.id === id ? { ...b, status } : b)));
      toast.success(`Booking marked ${status}`);
    } catch {
      toast.error("Status update failed");
    }
  };

  if (user === undefined) {
    return (
      <div className="min-h-screen bg-obsidian flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-ember animate-spin" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-obsidian flex items-center justify-center px-4 noise-overlay relative">
        <form onSubmit={login} className="w-full max-w-md bg-charcoal border border-white/10 rounded-sm p-8 sm:p-10" data-testid="admin-login-form">
          <div className="flex items-center gap-2 mb-8">
            <img src="/logo.jpg" alt="GoGo Auto Repair Services" className="h-11 w-auto rounded-sm" />
            <span className="font-display font-extrabold text-2xl uppercase tracking-wide">
              GoGo <span className="text-ember">Admin</span>
            </span>
          </div>
          <div className="space-y-5">
            <div>
              <label className="font-mono2 text-xs tracking-[0.25em] uppercase text-mist block mb-2">Email</label>
              <input data-testid="admin-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com" className={inputCls} required />
            </div>
            <div>
              <label className="font-mono2 text-xs tracking-[0.25em] uppercase text-mist block mb-2">Password</label>
              <input data-testid="admin-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className={inputCls} required />
            </div>
            {error && <p className="text-red-400 text-sm" data-testid="admin-login-error">{error}</p>}
            <button
              data-testid="admin-login-btn"
              type="submit"
              disabled={busy}
              className="w-full inline-flex items-center justify-center gap-2 bg-ember text-obsidian font-display font-bold uppercase tracking-wider text-lg px-7 py-4 rounded-sm hover:bg-[#9BE873] disabled:opacity-50 transition-all duration-200"
            >
              {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : null}
              {busy ? "Signing in…" : "Sign In"}
            </button>
            <a href="/" className="block text-center text-mist text-sm hover:text-ember transition-colors" data-testid="admin-back-home">← Back to site</a>
          </div>
        </form>
      </div>
    );
  }

  const pending = bookings.filter((b) => b.status === "requested").length;

  return (
    <div className="min-h-screen bg-obsidian noise-overlay relative" data-testid="admin-dashboard">
      <header className="border-b border-white/10 bg-charcoal/60 backdrop-blur-md sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-[72px] flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src="/logo.jpg" alt="GoGo Auto Repair Services" className="h-11 w-auto rounded-sm" />
            <span className="font-display font-extrabold text-2xl uppercase tracking-wide">
              GoGo <span className="text-ember">Admin</span>
            </span>
          </div>
          <div className="flex items-center gap-4">
            <span className="hidden sm:block text-mist text-sm" data-testid="admin-user-email">{user.email}</span>
            <button
              data-testid="admin-logout-btn"
              onClick={logout}
              className="inline-flex items-center gap-2 border border-white/20 text-mist hover:text-white hover:border-white/50 font-display font-bold uppercase tracking-wider text-sm px-4 py-2 rounded-sm transition-all"
            >
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
          <div className="bg-charcoal border border-white/10 rounded-sm p-6" data-testid="stat-total-bookings">
            <p className="font-mono2 text-[10px] tracking-[0.25em] uppercase text-mist">Total Bookings</p>
            <p className="font-display font-extrabold text-5xl mt-2">{bookings.length}</p>
          </div>
          <div className="bg-charcoal border border-ember/30 rounded-sm p-6" data-testid="stat-pending-bookings">
            <p className="font-mono2 text-[10px] tracking-[0.25em] uppercase text-ember">Awaiting Confirmation</p>
            <p className="font-display font-extrabold text-5xl mt-2 text-ember">{pending}</p>
          </div>
          <div className="bg-charcoal border border-white/10 rounded-sm p-6" data-testid="stat-messages">
            <p className="font-mono2 text-[10px] tracking-[0.25em] uppercase text-mist">Messages</p>
            <p className="font-display font-extrabold text-5xl mt-2">{messages.length}</p>
          </div>
        </div>

        <div className="flex gap-2 mb-6">
          <button
            data-testid="admin-tab-bookings"
            onClick={() => setTab("bookings")}
            className={`inline-flex items-center gap-2 font-display font-bold uppercase tracking-wider px-5 py-2.5 rounded-sm border transition-all ${tab === "bookings" ? "bg-ember text-obsidian border-ember" : "text-mist border-white/15 hover:border-white/40 hover:text-white"}`}
          >
            <CalendarCheck className="w-4 h-4" /> Bookings
          </button>
          <button
            data-testid="admin-tab-messages"
            onClick={() => setTab("messages")}
            className={`inline-flex items-center gap-2 font-display font-bold uppercase tracking-wider px-5 py-2.5 rounded-sm border transition-all ${tab === "messages" ? "bg-ember text-obsidian border-ember" : "text-mist border-white/15 hover:border-white/40 hover:text-white"}`}
          >
            <MessageSquare className="w-4 h-4" /> Messages
          </button>
        </div>

        {tab === "bookings" ? (
          <div className="space-y-4" data-testid="admin-bookings-list">
            {bookings.length === 0 && (
              <div className="bg-charcoal border border-white/10 rounded-sm p-12 text-center text-mist">
                <Inbox className="w-10 h-10 mx-auto mb-3 text-mist/50" /> No bookings yet.
              </div>
            )}
            {bookings.map((b) => (
              <div key={b.id} data-testid={`booking-row-${b.ref}`} className="bg-charcoal border border-white/10 rounded-sm p-6 hover:border-white/25 transition-colors">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="font-mono2 text-ember font-bold tracking-widest">{b.ref}</span>
                      <span className={`font-mono2 text-[10px] tracking-[0.2em] uppercase border px-2.5 py-1 rounded-sm ${STATUS_STYLES[b.status] || STATUS_STYLES.requested}`} data-testid={`booking-status-${b.ref}`}>
                        {b.status}
                      </span>
                    </div>
                    <p className="font-display font-bold uppercase text-xl mt-2 tracking-tight">{b.service}</p>
                    <p className="text-mist text-sm mt-1">{b.date} · {b.time}</p>
                  </div>
                  <div className="flex gap-2">
                    {b.status === "requested" && (
                      <button data-testid={`booking-confirm-${b.ref}`} onClick={() => setStatus(b.id, "confirmed")} className="font-display font-bold uppercase tracking-wider text-sm px-4 py-2 rounded-sm bg-volt/15 text-volt border border-volt/40 hover:bg-volt hover:text-obsidian transition-all">
                        Confirm
                      </button>
                    )}
                    {(b.status === "requested" || b.status === "confirmed") && (
                      <>
                        <button data-testid={`booking-complete-${b.ref}`} onClick={() => setStatus(b.id, "completed")} className="font-display font-bold uppercase tracking-wider text-sm px-4 py-2 rounded-sm bg-[#25D366]/10 text-[#25D366] border border-[#25D366]/40 hover:bg-[#25D366] hover:text-obsidian transition-all">
                          Complete
                        </button>
                        <button data-testid={`booking-cancel-${b.ref}`} onClick={() => setStatus(b.id, "cancelled")} className="font-display font-bold uppercase tracking-wider text-sm px-4 py-2 rounded-sm bg-red-500/10 text-red-400 border border-red-500/40 hover:bg-red-500 hover:text-white transition-all">
                          Cancel
                        </button>
                      </>
                    )}
                    {b.status === "completed" && (
                      <a
                        data-testid={`booking-review-${b.ref}`}
                        href={`https://api.whatsapp.com/send?phone=${(b.phone || "").replace(/\D/g, "")}&text=${encodeURIComponent(`Hi ${b.name}, thanks for choosing GoGoAuto Mobile Repair & Service! If you were happy with the ${b.service} on ${b.date}, a quick Google review would mean the world to our small business${REVIEW_LINK ? ` — ${REVIEW_LINK}` : "!"}`)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 font-display font-bold uppercase tracking-wider text-sm px-4 py-2 rounded-sm bg-[#25D366]/10 text-[#25D366] border border-[#25D366]/40 hover:bg-[#25D366] hover:text-obsidian transition-all"
                      >
                        <MessageCircle className="w-4 h-4" /> Ask Review
                      </a>
                    )}
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-white/5 grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
                  <div><p className="font-mono2 text-[10px] tracking-[0.2em] uppercase text-mist">Customer</p><p className="text-white mt-0.5">{b.name} · {b.phone}</p>{b.email && <p className="text-mist">{b.email}</p>}</div>
                  <div><p className="font-mono2 text-[10px] tracking-[0.2em] uppercase text-mist">Vehicle</p><p className="text-white mt-0.5">{b.vehicle}</p></div>
                  <div><p className="font-mono2 text-[10px] tracking-[0.2em] uppercase text-mist">Address</p><p className="text-white mt-0.5">{b.address}</p></div>
                </div>
                {b.notes && <p className="mt-3 text-sm text-mist italic">“{b.notes}”</p>}
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-4" data-testid="admin-messages-list">
            {messages.length === 0 && (
              <div className="bg-charcoal border border-white/10 rounded-sm p-12 text-center text-mist">
                <Inbox className="w-10 h-10 mx-auto mb-3 text-mist/50" /> No messages yet.
              </div>
            )}
            {messages.map((m) => (
              <div key={m.id} data-testid={`message-row-${m.id}`} className="bg-charcoal border border-white/10 rounded-sm p-6 hover:border-white/25 transition-colors">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <p className="font-display font-bold uppercase text-xl tracking-tight">{m.name}</p>
                  <p className="font-mono2 text-xs text-mist">{m.created_at ? new Date(m.created_at).toLocaleString() : ""}</p>
                </div>
                <p className="font-mono2 text-xs text-ember mt-1">{m.contact}</p>
                <p className="text-mist text-sm mt-3 leading-relaxed">{m.message}</p>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
